/**
 * Shared utilities for dashboard table actions:
 * Export (CSV / Excel / PDF), Filter (text search), Delete (local state).
 */

/**
 * Export an array of plain-object rows to a CSV file download.
 * Skips columns whose values are React elements.
 * @param {Array<{label:string, field:string}>} columns
 * @param {Array<Object>} rows  - plain object rows
 * @param {string} filename
 */
export function exportToCSV(columns, rows, filename = "export.csv") {
  const exportCols = columns.filter((c) => c.field !== "action" && c.field !== "checkbox");

  const header = exportCols.map((c) => `"${c.label}"`).join(",");

  const body = rows.map((row) =>
    exportCols
      .map((c) => {
        const val = row[c.field];
        if (val === null || val === undefined) return '""';
        if (typeof val === "object") return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
      })
      .join(",")
  );

  const csv = [header, ...body].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, filename);
}

/**
 * Export an array of plain-object rows to an Excel-compatible file.
 * Uses CSV with UTF-8 BOM and .xlsx extension that Excel opens natively.
 * @param {Array<{label:string, field:string}>} columns
 * @param {Array<Object>} rows
 * @param {string} filename
 */
export function exportToExcel(columns, rows, filename = "export.xlsx") {
  const exportCols = columns.filter((c) => c.field !== "action" && c.field !== "checkbox");

  const header = exportCols.map((c) => `"${c.label}"`).join("\t");

  const body = rows.map((row) =>
    exportCols
      .map((c) => {
        const val = row[c.field];
        if (val === null || val === undefined) return "";
        if (typeof val === "object") return "";
        return String(val).replace(/"/g, '""');
      })
      .join("\t")
  );

  const content = [header, ...body].join("\n");
  const blob = new Blob(["\uFEFF" + content], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  triggerDownload(blob, filename);
}

/**
 * Export an array of plain-object rows to a printable PDF via browser print dialog.
 * Opens a new window with a formatted HTML table and triggers print.
 * @param {Array<{label:string, field:string}>} columns
 * @param {Array<Object>} rows
 * @param {string} title - Report title
 */
export function exportToPDF(columns, rows, title = "Report") {
  const exportCols = columns.filter((c) => c.field !== "action" && c.field !== "checkbox");

  const headerHtml = exportCols.map((c) => `<th>${c.label}</th>`).join("");

  const bodyHtml = rows
    .map((row) => {
      const cells = exportCols
        .map((c) => {
          const val = row[c.field];
          if (val === null || val === undefined || typeof val === "object") return "<td>—</td>";
          return `<td>${String(val)}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
    h2 { color: #1E293B; margin-bottom: 8px; }
    p { color: #6B7280; font-size: 11px; margin: 0 0 16px; }
    table { border-collapse: collapse; width: 100%; }
    th { background-color: #1E293B; color: #fff; padding: 8px 10px; text-align: left; font-size: 11px; }
    td { padding: 7px 10px; border-bottom: 1px solid #E5E7EB; font-size: 11px; }
    tr:nth-child(even) td { background-color: #F9FAFB; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h2>${title}</h2>
  <p>Generated on ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
  <table>
    <thead><tr>${headerHtml}</tr></thead>
    <tbody>${bodyHtml}</tbody>
  </table>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 400);
  }
}

/**
 * Filter an array of plain-object rows by a search term (case-insensitive).
 * Searches across all string/number values.
 * @param {Array<Object>} rows
 * @param {string} searchText
 * @returns {Array<Object>}
 */
export function filterRows(rows, searchText) {
  if (!searchText || !searchText.trim()) return rows;
  const lower = searchText.toLowerCase();
  return rows.filter((row) =>
    Object.values(row).some((val) => {
      if (val === null || val === undefined) return false;
      if (typeof val === "object") return false;
      return String(val).toLowerCase().includes(lower);
    })
  );
}

// ── Helpers ──────────────────────────────────────────────

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
